// Categories.js — modernized top bar, categories relocated into a
// Live-styled fixed bottom nav (auto-hides on scroll), Go Live/Live
// entry row, and the footer merged into the end of the scrollable
// product list instead of colliding with the new fixed nav.
//
// LAYOUT MODEL
//  - Root is a strict column: [header] → [search panel | products/brand].
//  - Normal feed: root is overflow:hidden, the product area scrolls itself.
//  - Brand mode: root becomes the scroller, so header + UploadForm scroll
//    together; the fixed bottom nav is never affected.
//  - Searching: results get their own scroll panel between the header and
//    the fixed bottom nav. The feed/brand view is hidden with display:none
//    (not unmounted) so its scroll position and listeners survive.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from "../utils/apiClient";
import { client} from "../sanity/sanityClient";
import CategoriesDisplayTwo from './CategoriesDisplayTwo';
import CategoriesDisplay from './CategoriesDisplay';
import Cart from './Cart';
import RecommendedProducts from './RecommendedProducts';
import PWAShareButton from './PWAShareButton';
import UserControl from './UserControl';
import { useStateContext } from '../context/StateContext';
import { AiOutlineShopping } from 'react-icons/ai';
import { GoHome, GoSearch, GoArrowLeft } from 'react-icons/go';
import { FiRadio, FiVideo, FiArrowLeft } from 'react-icons/fi';
import writer from '../assets/writer.svg';
import truck2cargoIcon from '../assets/truck2cargo.png';
import Arrivalbadge from './Arrivalbadge.jpg';
import Loader from './Loader';
import SearchForm from './SearchForm';
import UploadForm from "./UploadForm";
import { Sendrequest, onMessageListener } from '../firebase';
import { useServerRole } from '../utils/permissions';
import {
  allowedusers, postInteractions, retrieveInteractions, getSubscriptionStatus, orderslist
} from "../utils/APIRoutes";
import './App.css';
import analytics from '../utils/analytics';

// Used only as a link TARGET for the support-chat shortcut — not as a
// permission check. Real admin gating below comes from useServerRole().
const ADMIN_USER_ID = process.env.REACT_APP_ADMIN_ID;

const categorieslinks = [
    { name: "Clothing", path: "/items/clothingandfashion" },
    { name: "Fashion", path: "/items/fashion" },
    { name: "Vehicles", path: "/items/vehicles" },
    { name: "Beauty", path: "/items/beauty" },
    { name: "Cuisines", path: "/items/cuisines" },
    { name: "GiftOptions", path: "/items/giftoptions" },
    { name: "Kitchen", path: "/items/kitchen" },
    { name: "Food&Beverages", path: "/items/foodandbeverages" },
    { name: "Crops", path: "/items/crops" },
    { name: "Energy&fuels", path: "/items/energyandfuel" },
    { name: "Kids&Toys", path: "/items/kidsandtoys" },
    { name: "AgriTech", path: "/items/agritech" },
    { name: "Services&Institutions", path: "/items/servicesandinstitutions" },
    { name: "Estate", path: "/items/estate" },
    { name: "Grocery", path: "/items/grocery" },
    { name: "UsedItems", path: "/items/useditems" },
    { name: "Livestock", path: "/items/livestock" },
    { name: "Jewelry&Accessories", path: "/items/jewelryandaccessories" },
    { name: "Sports", path: "/items/sports" },
    { name: "Arts", path: "/items/arts" },
    { name: "Hospitality", path: "/items/hospitality" },
    { name: "Home&Furniture", path: "/items/homeandfurnitures" },
    { name: "Electronics", path: "/items/electronics" },
    { name: "Hotels", path: "/items/hotels" },
    { name: "Medical&Suppliments", path: "/items/medical" },
    { name: "Salon", path: "/items/salon" },
    { name: "Hardware", path: "/items/hardware" },
    { name: "Construction", path: "/items/construction" },
    { name: "Books&Stationery", path: "/items/booksandstationery" },
    { name: "Events&nightclubs", path: "/items/eventsandnightclubs" },
  ];

// ---- Anonymous interaction store -------------------------------------
// Mirrors what the backend keeps for logged-in users: which products were
// viewed and in which category. Survives tab close, capped and expiring so
// it can't grow forever.
const ANON_KEY = 'anonymousInteractions';
const ANON_MAX = 100;                          // newest entries kept
const ANON_TTL_MS = 30 * 24 * 60 * 60 * 1000;  // 30 days

const readAnonInteractions = () => {
  try {
    // One-time migration of the old per-session data.
    const legacy = sessionStorage.getItem(ANON_KEY);
    if (legacy) {
      const merged = [
        ...(JSON.parse(localStorage.getItem(ANON_KEY)) || []),
        ...(JSON.parse(legacy) || []),
      ];
      localStorage.setItem(ANON_KEY, JSON.stringify(merged));
      sessionStorage.removeItem(ANON_KEY);
    }

    const raw = JSON.parse(localStorage.getItem(ANON_KEY)) || [];
    const cutoff = Date.now() - ANON_TTL_MS;
    return raw.filter(
      (i) => i && i.productId && i.category && new Date(i.timestamp).getTime() >= cutoff
    );
  } catch {
    return []; // storage blocked or corrupt: behave like "no history"
  }
};

const writeAnonInteractions = (list) => {
  try {
    localStorage.setItem(ANON_KEY, JSON.stringify(list.slice(-ANON_MAX)));
  } catch {
    /* private mode / quota: recommendations just fall back to popular */
  }
};

const addAnonInteraction = (productId, category) => {
  // One entry per product (latest wins), so repeat views don't skew things.
  const list = readAnonInteractions().filter((i) => i.productId !== productId);
  list.push({
    productId,
    category,
    interactionType: 'view',
    timestamp: new Date().toISOString(),
  });
  writeAnonInteractions(list);
};

// Matches Live.js's NavBar/NavItem palette exactly, so the bottom nav
// here reads as the same design language as the live-streaming views.
const LIVE_NAVY = "#0f1f3d";
const LIVE_ORANGE = "#f97316";
const LIVE_INACTIVE = "#cbd5e1";

// Two stacked rows (categories + go-live/live) — used both to size the
// fixed bar itself and to reserve matching bottom padding on every
// scrollable area so the last row of content never ends up hidden
// underneath it. Kept in sync with the actual padding values on both
// rows below — if you thin/thicken either row's padding, adjust this.
const BOTTOM_NAV_HEIGHT = 62;

// Facebook-like tight vertical rhythm between stacked feed sections
// (recommendation rail, message line, product grid).
const FEED_GAP = 4;

const Categories = ({
  categories,
  currentUser,
  isLoading,
  isFetchingMore,
  hasMore,
  loadMoreProducts,
  changeCategory,
  showCntcts,
  handleNearbyClick,
  fetchTraderProducts,
  nearBy,
  isTrader,
  traderProducts,
  message,
  popDelete,
  setPopDelete,
  handleCurrencyChange,
  selectedCurrency,
  currencyOptions,
  noProductsForCurrency,
  loadArrivals
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef();
  const { showCart, setShowCart, totalQuantities, onAdd } = useStateContext();
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchMode, setSearchMode] = useState('name');
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [isBrand, setIsBrand] = useState(false);
  const [products, setProducts] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [maxScrollDepth, setMaxScrollDepth] = useState(0); // Keep only maxScrollDepth
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 768px)").matches
  );

  // SECURITY: server-verified role, replacing the old
  // `currentUser._id === ADMIN_USER_ID` comparisons. This only affects
  // what the UI shows (e.g. skipping the subscription-required modal,
  // showing the full orders badge) — the backend independently
  // enforces the real rules on every endpoint this gates.
  const { isAdmin: viewerIsAdmin } = useServerRole();

  // Bottom nav auto-hide on scroll — starts visible, hides while
  // scrolling down through the product list, reappears on scroll up
  // or near the top of the page.
  const [showBottomNav, setShowBottomNav] = useState(true);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");

    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // Initialize analytics with user info
  useEffect(() => {
    if (currentUser) {
      analytics.setUser(currentUser);
    }

    // Track page view
    analytics.trackPageView('Categories');

    // Track PWA launch if applicable
    if (window.matchMedia('(display-mode: standalone)').matches) {
      analytics.trackPWALaunch();
    }

    // Setup scroll tracking
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      const scrollDepth = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);

      if (scrollDepth > maxScrollDepth) {
        setMaxScrollDepth(scrollDepth);

        // Track at key scroll points
        if (scrollDepth >= 25 && maxScrollDepth < 25) {
          analytics.trackScroll(25);
        }
        if (scrollDepth >= 50 && maxScrollDepth < 50) {
          analytics.trackScroll(50);
        }
        if (scrollDepth >= 75 && maxScrollDepth < 75) {
          analytics.trackScroll(75);
        }
        if (scrollDepth >= 90 && maxScrollDepth < 90) {
          analytics.trackScroll(90);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Track time spent on page periodically
    const timeInterval = setInterval(() => {
      const timeSpent = Math.floor((Date.now() - analytics.pageStartTime) / 1000);
      if (timeSpent >= 30) {
        analytics.trackTimeSpent(timeSpent);
      }
    }, 30000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(timeInterval);

      // Track exit
      const totalTime = Math.floor((Date.now() - analytics.pageStartTime) / 1000);
      analytics.trackTimeSpent(totalTime);
    };
  }, [currentUser, maxScrollDepth]);

  // Track PWA installation
  useEffect(() => {
    // First check if we're in a TWA (Trusted Web Activity)
    const isTWA = navigator.userAgent.toLowerCase().includes('wv') ||
                 document.referrer.includes('android-app://');

    if (isTWA) return; // Skip installation logic for TWA

    const handler = (e) => {
      e.preventDefault();
      console.log('BeforeInstallPrompt event fired');
      analytics.trackClick('pwa_install_prompt', { action: 'shown' });
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Cleanup
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      analytics.trackClick('pwa_install_button', { action: 'clicked' });
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        analytics.trackPWAInstall('accepted');
        console.log('User accepted the install prompt');
      } else {
        analytics.trackPWAInstall('declined');
        console.log('User dismissed the install prompt');
      }
    } catch (error) {
      console.error('Installation error:', error);
      analytics.trackPWAInstall('error');
      analytics.trackError(error, { context: 'pwa_installation' });
    } finally {
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  useEffect(() => {
    const fetchAllowedUsers = async () => {
      try {
        const { data } = await apiClient.get(allowedusers);

        const users = Array.isArray(data)
          ? data
          : data.users || [];

        setAllowedUsers(users.map(user => user._id));
      } catch (error) {
        console.error(
          "Error fetching requested users:",
          error.response?.data?.msg || error.message
        );

        analytics.trackError(error, {
          context: "fetch_allowed_users",
        });
      }
    };

    fetchAllowedUsers();
  }, []);

  const isUserAllowed = currentUser?._id && allowedUsers.includes(currentUser._id);

  // Improved useEffect for subscription check
  useEffect(() => {
    const checkSubscription = async () => {
      if (!currentUser?._id) return;

      try {
        setSubscriptionLoading(true);
        const { data } = await apiClient.get(`${getSubscriptionStatus}/${currentUser._id}`);
        setSubscriptionStatus(data.subscription);

        // Track subscription view if user has one
        if (data.subscription) {
          analytics.trackSubscriptionView(data.subscription.type);
        }
      } catch (error) {
        console.error("Error checking subscription:", error.response?.data?.msg || error.message);
        analytics.trackError(error, { context: 'check_subscription' });
      } finally {
        setSubscriptionLoading(false);
      }
    };

    checkSubscription();
  }, [currentUser]);


// Anonymous visitors never get a currency set by Chat, so fall back the
// same way fetchCategories does.
const activeCurrency =
  selectedCurrency || localStorage.getItem('selectedCurrency') || 'TZS';

const fetchAnonymousRecommendations = useCallback(async () => {
  const shuffleTake = (list, n = 10) =>
    [...list].sort(() => Math.random() - 0.5).slice(0, n);

  const popularFallback = async () => {
    try {
      return await client.fetch(
        `*[_type in $types && currency == $currency] | order(popularity desc) [0...10]`,
        { types: ["electronics", "clothingandfashion", "beauty"], currency: activeCurrency }
      );
    } catch (err) {
      console.error('[anon] popular fallback failed:', err?.message || err);
      return [];
    }
  };

  const interactions = readAnonInteractions();
  if (interactions.length === 0) return popularFallback();

  // Same shape the backend returns: unique productIds + unique categories.
  const viewedIds = [...new Set(
    interactions.map((i) => i.productId).filter((v) => typeof v === 'string' && v)
  )];
  const types = [...new Set(
    interactions.map((i) => i.category).filter((v) => typeof v === 'string' && v)
  )];
  if (types.length === 0) return popularFallback();

  try {
    const unseen = await client.fetch(
      `*[_type in $types && !(_id in $viewedIds) && currency == $currency][0...60]`,
      { types, viewedIds, currency: activeCurrency }
    );
    if (unseen.length > 0) return shuffleTake(unseen);

    const any = await client.fetch(
      `*[_type in $types && currency == $currency][0...60]`,
      { types, currency: activeCurrency }
    );
    return any.length > 0 ? shuffleTake(any) : popularFallback();
  } catch (err) {
    console.error('[anon] sanity query failed:', err?.message, err?.response?.body || err);
    return popularFallback();
  }
}, [activeCurrency]);


  const fetchRecommendations = useCallback(
  async (userId) => {
    
    // ---------- Anonymous visitor: local history ----------
    if (!userId) {
      return fetchAnonymousRecommendations();
    }

    // ---------- Logged-in user: backend history ----------
    try {
      const response = await apiClient.get(`${retrieveInteractions}/${userId}`);

      if (response.status !== 200) {
        console.warn(
          'Failed to fetch interactions:',
          response.data?.error || response.data?.message
        );
        return [];
      }

      const { productIds = [], categories = [] } = response.data || {};

      // Safely handle users without interaction history.
      if (!Array.isArray(categories) || categories.length === 0) {
        return [];
      }

      const allProducts = await client.fetch(
        `*[_type in $categories && !(_id in $productIds) && currency == $currency]`,
        { categories, productIds: Array.isArray(productIds) ? productIds : [], currency: activeCurrency }
      );

      return [...allProducts].sort(() => Math.random() - 0.5).slice(0, 10);
    } catch (error) {
      console.error(
        'Error fetching recommendations:',
        error.response?.data?.error || error.response?.data?.message || error.message
      );
      analytics.trackError(error, { context: 'fetch_recommendations' });
      return [];
    }
  },
  [activeCurrency, fetchAnonymousRecommendations]);

  useEffect(() => {
  let cancelled = false;
  const userId = currentUser?._id || null;

  fetchRecommendations(userId).then((list) => {
    if (!cancelled) setProducts(list);
  });

  return () => { cancelled = true; };
}, [currentUser, fetchRecommendations]);


useEffect(() => {
  if (!currentUser?._id) return;
  const local = readAnonInteractions();
  if (local.length === 0) return;

  let cancelled = false;
  (async () => {
    const batch = local.slice(-50);
    const results = await Promise.allSettled(
      batch.map((i) =>
        apiClient.post(postInteractions, {
          userId: currentUser._id,
          productId: i.productId,
          interactionType: i.interactionType || 'view',
          category: i.category,
        })
      )
    );
    if (cancelled) return;

    // Drop what synced; keep anything that failed for the next attempt.
    const failedIds = new Set(
      batch.filter((_, idx) => results[idx].status === 'rejected').map((i) => i.productId)
    );
    const leftover = local.filter(
      (i) => !batch.some((b) => b.productId === i.productId) || failedIds.has(i.productId)
    );
    writeAnonInteractions(leftover);
  })();

  return () => { cancelled = true; };
}, [currentUser?._id]);


  // Updated handleProductView with analytics
const handleProductView = async (productId, category) => {
  // Anonymous: record immediately, no product lookup required.
  if (!currentUser) {
    addAnonInteraction(productId, category);
    console.log('anon interaction saved', productId, category); // remove after testing
  }

  const product = categories.find(p => p._id === productId) ||
                  products.find(p => p._id === productId);

  if (product) {
    analytics.trackProductView(product);

    if (currentUser) {
      try {
        await apiClient.post(postInteractions, {
          userId: currentUser._id, productId, interactionType: "view", category
        });
      } catch (error) {
        console.error("Error posting interaction:", error);
        analytics.trackError(error, { context: 'post_interaction' });
      }
    }
  }
};

  // Track category clicks
  const handleCategoryClick = (categoryName, path) => {
    setSelectedCategory(categoryName);
    analytics.trackCategoryView({
      _id: path,
      name: categoryName,
      slug: { current: path.replace('/items/', '') }
    });
    analytics.trackClick('category_link', { category: categoryName });
  };


  // Track search
  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    const debounce = setTimeout(async () => {
      try {
        let query;

        // 🔍 NAME SEARCH → flexible + ranked
        if (searchMode === "name") {
          query = `*[
            _type in [
              "clothingandfashion","electronics","arts","fashion","salon","grocery","beauty","crops",
              "servicesandinstitutions","energyandfuel","toys","sports","kitchen","hotels","hardware",
              "books","estate","foodandbeverages","agritech","giftoptions","cuisines","eventsandnightclubs",
              "homeandfurnitures","vehicles","jewelryandaccessories","medical","useditems","construction",
              "kidsandtoys","hospitality","booksandstationery"
            ]
            && currency == $currency
          ]
          | score(name match $search)
          | order(_score desc)
          [0...20]`;
        }

        // 🌍 LOCATION SEARCH → exact match, NO scoring
        if (searchMode === "location") {
          query = `*[
            _type in [
              "clothingandfashion","electronics","arts","fashion","salon","grocery","beauty","crops",
              "servicesandinstitutions","energyandfuel","toys","sports","kitchen","hotels","hardware",
              "books","estate","foodandbeverages","agritech","giftoptions","cuisines","eventsandnightclubs",
              "homeandfurnitures","vehicles","jewelryandaccessories","medical","useditems","construction",
              "kidsandtoys","hospitality","booksandstationery"
            ]
            && currency == $currency
            && lower(location) match lower($search)
          ][0...50]`;
        }

        const results = await client.fetch(query, {
          search: `*${trimmedQuery}*`, // used only for name
          currency: activeCurrency,
        });

        setSearchResults(results);
        setSearching(true);

        analytics.trackSearch(trimmedQuery, results.length, {
          currency: activeCurrency,
          mode: searchMode,
        });

      } catch (error) {
        console.error("Search error:", error);
        analytics.trackError(error, { context: "sanity_search" });
      }
    }, 400);

    return () => clearTimeout(debounce);
  }, [searchQuery, searchMode, activeCurrency]);


  // Track add to cart from parent component
  const handleAddToCart = (product, index) => {
    if (!product) {
      analytics.trackError(new Error('Product information missing'), {
        context: 'add_to_cart'
      });
      return;
    }

    analytics.trackAddToCart(product, 1);
    analytics.trackClick('buy_button', {
      productId: product._id,
      productName: product.name
    });

    const productForCart = {
      ...product,
      quantity: 1,
      selectedIndex: index
    };

    const result = onAdd(productForCart, 1, index);
    if (result?.success) {
      setShowCart(true);
      analytics.trackClick('cart_icon', { action: 'open_cart' });
    }
  };

  // Track cart opens/closes
  useEffect(() => {
    if (showCart) {
      analytics.trackClick('cart_modal', { action: 'open', itemCount: totalQuantities });
    }
  }, [showCart, totalQuantities]);

  useEffect(() => {
    if (products.length > 0) {
      // Track recommendations shown
      analytics.track('recommendations_shown', {
        recommendationCount: products.length,
        recommendationTypes: [...new Set(products.map(p => p._type))]
      });
    }
  }, [products]);

  const handlePostClick = () => {
    analytics.trackClick('post_button', {
      userLoggedIn: !!currentUser,
      userAllowed: isUserAllowed,
      hasSubscription: subscriptionStatus?.active
    });

    if (!currentUser) {
      navigate("/login");
    } else if (subscriptionLoading) {
      return;
    } else if (isUserAllowed) {
      // The add-product screen/backend must independently re-check
      // subscription/admin status — this bypass only decides whether
      // we skip straight there instead of showing the modal first.
      if (subscriptionStatus?.active || viewerIsAdmin) {
        navigate(`/addproduct/${currentUser._id}`);
      } else {
        setShowSubscriptionModal(true);
        analytics.trackSubscriptionView('required');
      }
    } else {
      navigate(`/userrequests/${currentUser._id}`);
    }
  };
  const handletoOrders=()=>{
    navigate(`/orders/${currentUser?._id}`);

  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Sendrequest();
        const payload = await onMessageListener();
        console.log({
          title: payload?.title,
          body: payload?.body,
        });

        // Track notification
        if (payload?.title) {
          analytics.track('notification_received', {
            notificationTitle: payload.title,
            notificationBody: payload.body
          });
        }
      } catch (err) {
        console.error('Failed:', err);
        analytics.trackError(err, { context: 'firebase_messaging' });
      }
    };
    fetchData();
  }, []);

  const goBrand = () => {
    analytics.trackClick('brand_toggle', { newState: !isBrand });
    setIsBrand(!isBrand);
  };

  const handleReload = () => {
    analytics.trackClick('reload_button', { action: 'reload_page' });
    window.location.reload();
  };

  const handleCurrencyChangeWithAnalytics = (e) => {
    const newCurrency = e.target.value;
    analytics.trackClick('currency_selector', {
      previousCurrency: selectedCurrency,
      newCurrency: newCurrency
    });
    handleCurrencyChange(e);
  };

  const handleNearbyClickWithAnalytics = () => {
    analytics.trackClick('nearby_button', {
      currentState: nearBy,
      action: nearBy ? 'exit_nearby' : 'enter_nearby'
    });
    handleNearbyClick();
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollElement = scrollRef.current || window;
      const scrollTop = scrollElement === window
        ? window.pageYOffset || document.documentElement.scrollTop
        : scrollElement.scrollTop;
      const scrollHeight = scrollElement === window
        ? document.documentElement.scrollHeight
        : scrollElement.scrollHeight;
      const clientHeight = scrollElement === window
        ? document.documentElement.clientHeight
        : scrollElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMoreProducts();
      }

      // Bottom nav auto-hide: hide while actively scrolling down past
      // a small threshold (avoids flicker on tiny/inertial deltas),
      // reveal on scroll up, and always show it again near the very
      // top of the page regardless of direction.
      const delta = scrollTop - lastScrollTopRef.current;
      if (scrollTop < 40) {
        setShowBottomNav(true);
      } else if (delta > 8) {
        setShowBottomNav(false);
      } else if (delta < -8) {
        setShowBottomNav(true);
      }
      lastScrollTopRef.current = scrollTop;
    };

    const targetElement = scrollRef.current || window;
    targetElement.addEventListener('scroll', handleScroll);

    return () => targetElement.removeEventListener('scroll', handleScroll);
  }, [loadMoreProducts]);

  useEffect(() => {
    const fetchUserOrders = async () => {
      if (!currentUser?._id) return;

      try {
        // FIX: /api/ordered now requires a session (auth middleware) and
        // returns { success, orders, isAdmin } with each order already
        // scoped server-side to exactly what this viewer should see —
        // admin: everything, everyone else: only orders they're the
        // buyer/agent/a seller on. No client-side re-filtering by id
        // needed anymore; that used to over-fetch every user's orders
        // to the browser and just hide most of them in the UI.
        const { data } = await apiClient.get(orderslist);
        const userOrders = data.orders || [];

        // 🔥 Count only NEW orders (not processed or shipped)
        const newOrders = userOrders.filter(order => order.status !== 'processed' && order.status !== 'shipped');

        setNewOrdersCount(newOrders.length);

      } catch (err) {
        console.error("Error fetching orders for badge:", err);
      }
    };

    fetchUserOrders();

    // Optional: auto refresh every 10s
    const interval = setInterval(fetchUserOrders, 10000);

    return () => clearInterval(interval);
  }, [currentUser]);

  // Small reusable pill-button style for the compact secondary
  // control row, so every button there is visually consistent instead
  // of each having its own bespoke inline style like before.
  const pillButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    height: '28px',
    padding: '0 12px',
    borderRadius: '14px',
    border: 'none',
    background: 'rgba(255,255,255,0.85)',
    color: '#111',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  return (
    <div
      className="app-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100vh',            // fallback for older browsers
        maxHeight: '100dvh',        // real visible height on mobile (URL bar aware)
        position: 'relative',
        // Normal feed: only the product area scrolls.
        // Brand mode: the whole page (header + UploadForm) scrolls.
      }}
    >
      {/* Tell modern browsers to use the dynamic viewport height. Inline
          styles can't express two `height` values, so we do it here. */}
      <style>{`.app-container{height:100vh;}@supports (height:100dvh){.app-container{height:100dvh !important;}}`}</style>

      {/* ================= COMPACT TOP HEADER (never shrinks) ================= */}
      <div style={{ flexShrink: 0 }}>
        {/* Row 1: home, search, share, cart — compact primary actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px 2px',
          }}
        >
          <Link
            to="/"
            style={{
              color: '#111',
              borderRadius: '50%',
              background: 'white',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              textDecoration: 'none',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}
            onClick={() =>
              analytics.trackClick('home_link', { page: 'home' })
            }
          >
            <GoHome size={18} />
          </Link>

          <div
            className="search-form"
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              height: '30px',
              borderRadius: '15px',
              backgroundColor: 'white',
              padding: '0 9px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              minWidth: 0,
              border:'2px solid #0f1f3d'
            }}
          >
            <GoSearch
              color="#888"
              size={15}
              style={{ flexShrink: 0 }}
            />

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              required
              placeholder={`Search BridgenApp`}
              maxLength={100}
              style={{
                flex: 1,
                minWidth: 0,
                height: '100%',
                border: 'none',
                outline: 'none',
                marginLeft: '6px',
                fontSize: '12px',
              }}
              onFocus={() =>
                analytics.trackClick('search_input', {
                  action: 'focus',
                })
              }
            />

            <select
              value={searchMode}
              onChange={(e) => setSearchMode(e.target.value)}
              style={{
                height: '21px',
                marginLeft: '4px',
                borderRadius: '10px',
                border: 'none',
                padding: '0 3px',
                fontSize: '10px',
                backgroundColor: '#f1f1f1',
                flexShrink: 0,
              }}
            >
              <option value="name">By Name</option>
              <option value="location">By Location</option>
            </select>

            {searchQuery && (
              <button
                onClick={() => {
                  analytics.trackClick('search_clear', {
                    query: searchQuery,
                  });
                  setSearchQuery('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginLeft: '4px',
                  padding: 0,
                  fontSize: '15px',
                  lineHeight: 1,
                  color: '#999',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            )}
          </div>

          <PWAShareButton
            onShare={() =>
              analytics.trackClick('pwa_share', {
                platform: 'web_share',
              })
            }
          />

          <button
            type="button"
            className="cart-icon"
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'white',
              border: 'none',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              flexShrink: 0,
              cursor: 'pointer',
              padding: 0,
            }}
            onClick={() => {
              analytics.trackClick('cart_icon', {
                action: 'open_cart',
                itemCount: totalQuantities,
              });
              setShowCart(true);
            }}
          >
            <AiOutlineShopping
              color="#111"
              size={17}
            />

            {totalQuantities > 0 && (
              <span
                className="cart-item-qty"
                style={{
                  position: 'absolute',
                  top: '-3px',
                  right: '-3px',
                  background: 'red',
                  color: 'white',
                  borderRadius: '50%',
                  fontSize: '9px',
                  minWidth: '15px',
                  height: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {totalQuantities}
              </span>
            )}
          </button>

          <div className={`modal ${showCart ? 'active' : ''}`}>
            <Cart
              onTrackRemoveFromCart={(product) =>
                analytics.trackRemoveFromCart(product)
              }
              onTrackPurchase={(order) =>
                analytics.trackPurchase(order)
              }
            />
          </div>
        </div>

        {/* Row 2: compact secondary controls */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            height:isBrand?'30px':'auto',
            minHeight: isBrand?'30px':'auto',
            gap: '2px',
            padding: '0 4px 1px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          className="hide-scrollbar"
        >
          <select
            value={selectedCurrency ?? 'TZS'}
            onChange={handleCurrencyChangeWithAnalytics}
            style={{
              ...pillButtonStyle,
              paddingRight: '3px',
              paddingLeft: '5px',
              paddingTop: '2px',
              paddingBottom: '2px',
              fontSize: '11px',
              minHeight: '24px',
            }}
          >
            {currencyOptions.map((currency) => (
              <option
                key={currency.code}
                value={currency.code}
              >
                {currency.code}
              </option>
            ))}
          </select>

          <button
            onClick={handlePostClick}
            style={{
              ...pillButtonStyle,
              padding: '2px 8px',
              fontSize: '11px',
              minHeight: '24px',
            }}
          >
            Post
          </button>

          <button
            onClick={goBrand}
            style={{
              ...pillButtonStyle,
              padding: '2px 8px',
              fontSize: '11px',
              minHeight: '24px',
            }}
          >
            {isBrand ? <GoArrowLeft size={18} /> : 'Branding'}
          </button>

          {nearBy ? (
            <button
              onClick={handleReload}
              style={{
                ...pillButtonStyle,
                padding: '2px 8px',
                fontSize: '11px',
                minHeight: '24px',
              }}
            >
              <GoArrowLeft />
            </button>
          ) : (
            <button
              onClick={handleNearbyClickWithAnalytics}
              style={{
                ...pillButtonStyle,
                padding: '2px 8px',
                fontSize: '11px',
                minHeight: '24px',
              }}
            >
              Nearby
            </button>
          )}

          <div
            style={{
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <button
              onClick={handletoOrders}
              style={{
                ...pillButtonStyle,
                padding: '2px 8px',
                fontSize: '11px',
                minHeight: '24px',
              }}
            >
              Orders
            </button>

            {newOrdersCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  backgroundColor: 'red',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '1px 4px',
                  fontSize: '8px',
                  fontWeight: 'bold',
                  minWidth: '14px',
                  height: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {newOrdersCount}
              </span>
            )}
          </div>
                    {/* Truck2Cargo — new category, image used as the link name */}
          <Link
            to="/items/truck2cargo"
            aria-label="Truck2Cargo"
            title="Truck2Cargo"
            onClick={() =>
              handleCategoryClick('Truck2Cargo', '/items/truck2cargo')
            }
            style={{
              ...pillButtonStyle,
              padding: '0 6px',
              minHeight: '24px',
              height: '24px',
              textDecoration: 'none',
            }}
          >
            <img
              src={truck2cargoIcon}
              alt="Truck2Cargo"
              style={{
                height: '20px',
                width: '20px',
                borderRadius: '50%',
                objectFit: 'cover',
                display: 'block',
                flexShrink: 0,
              }}
            />
          </Link>
        </div>

        {showInstallButton && (
          <button
            onClick={handleInstallClick}
            style={{
              position: 'fixed',
              top: '12px',
              right: '12px',
              padding: '7px 11px',
              backgroundColor: 'white',
              color: '#1f2937',
              border: 'none',
              borderRadius: '15px',
              fontSize: '12px',
              cursor: 'pointer',
              zIndex: 30,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          >
            Install BridgenApp
          </button>
        )}
      </div>

      {/* ================= SEARCH RESULTS PANEL =================
          Its own scroller, sitting between the pinned header and the
          fixed bottom nav. Rendered only while searching. */}
      {searching && (
        <div
          className="hide-scrollbar"
          style={{
            flex: '1 1 0',
            minHeight: 0,                    // lets the flex child shrink and scroll
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehaviorY: 'contain',
            WebkitOverflowScrolling: 'touch',
            padding: `4px 0 ${BOTTOM_NAV_HEIGHT + 8}px`, // keep last result clear of the fixed nav
            background: '#f0f2f5',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '1400px',
              margin: '0 auto',
              background: 'white',
              borderRadius: '8px',
            }}
          >
            <SearchForm searchResults={searchResults} />
          </div>
        </div>
      )}

      {/* ================= PRODUCTS / BRAND AREA =================
          Hidden (not unmounted) while searching so the feed keeps its
          scroll position and listeners.
          - Feed mode: fills ALL remaining height; the feed scrolls inside.
          - Brand mode: natural height; the ROOT scrolls everything. */}
      <div
        style={{
          display: searching ? 'none' : 'flex',
          flexDirection: 'column',
          flex: isBrand ? '0 0 auto' : '1 1 0',
          minHeight: isBrand ? undefined : 0,
        }}
      >
        {isBrand ? (
          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              // reserve room so the last fields/buttons are never hidden under the fixed nav
              paddingBottom: `${BOTTOM_NAV_HEIGHT + 12}px`,
            }}
          >
            <UploadForm
              fetchTraderProducts={fetchTraderProducts}
              isTrader={isTrader}
              traderProducts={traderProducts}
              currentUser={currentUser}
              onTrackUpload={() => analytics.trackClick('upload_form', { action: 'open' })}
            />
          </div>
        ) : (
          <div
            className="search-results"
            style={{
              flex: '1 1 0',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoading && categories.length <= 0 ? (
              <Loader />
            ) : !isLoading && noProductsForCurrency ? (
              <div className='displayCenter' style={{ textAlign: 'center', paddingBottom: BOTTOM_NAV_HEIGHT }}>
                <h5>No products listed in this currency</h5>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  Try switching to a different currency, or check back later.
                </p>
              </div>
            ) : !isLoading && categories.length <= 0 ? (
              <div className='displayCenter' style={{ textAlign: 'center', paddingBottom: BOTTOM_NAV_HEIGHT }}>
                <img src={writer} width={300} height={300} alt="no-internet" />
                <h5>No Products!</h5>
                <p>Check your internet connection & refresh.</p><br />
                <p style={{ fontSize: '10px' }}>Alternatively switch to currency or category with products</p>
              </div>
            ) : (
              <div className="hide-scrollbar"
                ref={scrollRef}
                style={{
                  flex: '1 1 0',
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  overscrollBehaviorY: 'contain',
                  WebkitOverflowScrolling: 'touch',
                  paddingBottom: `${BOTTOM_NAV_HEIGHT}px`,
                  ...(isMobile && {
                    position: 'relative',
                    width: '100vw',
                    marginLeft: '-1px',
                    paddingRight: '1px'
                  })
                }}>
                {/* Tight Facebook-like vertical stack: every direct child
                    sits FEED_GAP px from the one above/below it. */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${FEED_GAP}px`,
                    width: '100%',
                  }}
                >
                  <RecommendedProducts products={products}
                    handleProductView={handleProductView}
                  />
                  {message === "unregistered" ? (
                    <p style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      margin: "0",
                      borderRadius: '1px',
                      background: 'linear-gradient(to bottom right, #0056b3, pink)',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      color: 'transparent',
                      fontWeight: 'bold',
                      justifyContent: 'center'
                    }}>
                      Kindly
                      <Link
                        to="/login"
                        style={{
                          color: 'blue',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          marginLeft: "2px",
                          marginRight: '3px'
                        }}
                        onClick={() => analytics.trackClick('login_prompt', { context: 'unregistered' })}
                      >
                        Sign in
                      </Link>
                      to enjoy all services!
                    </p>
                  ) : message ? (
                    <p style={{
                      display: 'flex',
                      flexDirection: 'row',
                      margin: "0",
                      borderRadius: '1px',
                      background: 'linear-gradient(to bottom right, #0056b3, pink)',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      color: 'transparent',
                      fontWeight: 'bold',
                      justifyContent: 'center'
                    }}>
                      {message} <Link style={{ textDecoration: 'none', marginLeft: "4px" }}
                        to={`/chat/${ADMIN_USER_ID}`}
                        onClick={() => analytics.trackClick('customer_support', { supportId: ADMIN_USER_ID })}
                      >
                        Chat with Us
                      </Link>
                    </p>
                  ) : null}

                  {popDelete && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1000,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(0,0,0,0.5)'
                    }}>
                      <UserControl
                        currentUser={currentUser}
                        setPopDelete={setPopDelete}
                        onTrackDelete={() => analytics.trackClick('delete_account', { action: 'modal_open' })}
                      />
                    </div>
                  )}

                  {isMobile ? (
                    <CategoriesDisplayTwo
                      categories={categories}
                      handleProductView={handleProductView}
                      handleAddToCart={handleAddToCart}
                      nearBy={nearBy}
                    />
                  ) : (
                    <CategoriesDisplay
                      categories={categories}
                      handleProductView={handleProductView}
                      handleAddToCart={handleAddToCart}
                    />
                  )}

                  {isFetchingMore && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
                      <Loader />
                    </div>
                  )}

                  {!isFetchingMore && !hasMore && categories.length > 0 && (
                    <div style={{ textAlign: 'center', padding: '12px', color: '#666' }}>
                      No more products to load
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ================= FIXED BOTTOM NAV — Live.js-styled,
          auto-hides on scroll down in the feed, reappears on scroll up.
          Always visible in brand mode and while searching, since the
          auto-hide listener only watches the feed scroller. ================= */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          background: LIVE_NAVY,
          transform: (showBottomNav || searching) ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease',
        }}
      >
        {/* Row 1: horizontally scrollable categories */}
        <div
          className="hide-scrollbar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
            padding: '5px 12px 2px',
            WebkitOverflowScrolling: 'touch',

            // Hide scrollbar in Firefox
            scrollbarWidth: 'none',

            // Hide scrollbar in IE/old Edge
            msOverflowStyle: 'none',
          }}
        >
          <img
            src={Arrivalbadge}
            alt="Arrivalbadge"
            style={{
              width: 40,
              height: 20,
              borderRadius: 6,
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onClick={loadArrivals}
          />

          {categorieslinks.map((category, index) => (
            <Link
              key={index}
              to={category.path}
              style={{
                color:
                  selectedCategory === category.name
                    ? '#fff'
                    : LIVE_INACTIVE,

                backgroundColor:
                  selectedCategory === category.name
                    ? LIVE_ORANGE
                    : 'transparent',

                fontSize: '11px',

                fontWeight:
                  selectedCategory === category.name
                    ? 700
                    : 500,

                textDecoration: 'none',
                padding: '4px 10px',
                borderRadius: '12px',
                flexShrink: 0,
                transition: 'all 0.2s ease',
              }}
              onClick={() =>
                handleCategoryClick(
                  category.name,
                  category.path
                )
              }
            >
              {category.name}
            </Link>
          ))}
        </div>

        {/* Row 2: Go Live / Live entry points. space-evenly across the
            full-width fixed bar fits any mobile width with no scrolling
            and spreads out proportionally on tablet/desktop. */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            width: '100%',
            padding: '2px 0 6px',
          }}
        >
          {isBrand?(
            <button
            onClick={goBrand}
            style={{
              display: 'flex',
              alignItems: 'center',
              background:'transparent',
              border:'none',
              gap: '6px',
              textDecoration: 'none',
            }}
          >
            <FiArrowLeft size={18}
              style={{ color: '#fff', }} />
            </button>

            ):(
            <Link
            to="/golive"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: LIVE_ORANGE,
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 700,
            }}
            onClick={() => analytics.trackClick('golive_link', { source: 'categories_bottom_nav' })}
          >
            <FiVideo size={16} />
            Go Live
          </Link>
            )}
          <Link
            to="/live-discovery"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: LIVE_INACTIVE,
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 700,
            }}
            onClick={() => analytics.trackClick('live_link', { source: 'categories_bottom_nav' })}
          >
            <FiRadio size={16} />
            Live Now
          </Link>
          <Link
            to="/replays"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: LIVE_INACTIVE,
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 700,
            }}
            onClick={() => analytics.trackClick('live_link', { source: 'categories_bottom_nav' })}
          >
            <FiRadio size={16} />
            View Ended
          </Link>
        </div>
      </div>

      {showSubscriptionModal && (
        <div className="subscription-modal">
          <div className="modal-content">
            <h3>Subscription Required</h3>
            {subscriptionStatus?.type === 'trial' ? (
              <p>Your free trial has ended. Please subscribe to continue posting products.</p>
            ) : (
              <p>You need an active subscription to post products.</p>
            )}
            <Link
              to={`/subscription/${currentUser._id}`}
              className="subscribe-link"
              onClick={() => {
                analytics.trackClick('subscription_link', {
                  action: 'view_options',
                  fromModal: true
                });
                setShowSubscriptionModal(false);
              }}
            >
              View Subscription Options
            </Link>
            <button
              onClick={() => {
                analytics.trackClick('modal_close', { modal: 'subscription_required' });
                setShowSubscriptionModal(false);
              }}
              className="close-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;